import api, {

  buildCompanyScopeHeaders,
  buildActivityScopeHeaders,
} from "./api";


export const DEFAULT_INVENTORY_PAGE_SIZE = 50;

export const INVENTORY_STATUSES = Object.freeze([
  "in_stock",
  "low_stock",
  "out_of_stock",
]);

export const INVENTORY_SORT_FIELDS = Object.freeze([
  "product",
  "stock",
  "available",
  "coverage",
  "demand",
  "snapshot",
]);

export const INVENTORY_SORT_DIRECTIONS = Object.freeze([
  "asc",
  "desc",
]);


function normalizePositiveInteger(
  value,
  fallback,
) {
  const parsed = Number.parseInt(value, 10);

  if (
    !Number.isInteger(parsed)
    || parsed <= 0
  ) {
    return fallback;
  }

  return parsed;
}


function normalizeOptionalString(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
}


function normalizeSupportedValue(
  value,
  allowedValues,
  fallback,
) {
  const normalized = normalizeOptionalString(
    value,
  ).toLowerCase();

  if (!allowedValues.includes(normalized)) {
    return fallback;
  }

  return normalized;
}


function requireNonEmptyString(
  value,
  fieldName,
) {
  if (
    typeof value !== "string"
    || !value.trim()
  ) {
    throw new TypeError(
      `${fieldName} must be a non-empty string.`,
    );
  }

  return value.trim();
}


export function buildInventoryListParams({
  page = 1,
  pageSize = DEFAULT_INVENTORY_PAGE_SIZE,
  search = "",
  supplierUuid = "",
  productUuid = "",
  status = "",
  sort = "product",
  direction = "asc",
} = {}) {
  const params = {
    page: normalizePositiveInteger(
      page,
      1,
    ),
    page_size: normalizePositiveInteger(
      pageSize,
      DEFAULT_INVENTORY_PAGE_SIZE,
    ),
    sort: normalizeSupportedValue(
      sort,
      INVENTORY_SORT_FIELDS,
      "product",
    ),
    direction: normalizeSupportedValue(
      direction,
      INVENTORY_SORT_DIRECTIONS,
      "asc",
    ),
  };

  const normalizedSearch = normalizeOptionalString(
    search,
  );

  if (normalizedSearch) {
    params.search = normalizedSearch;
  }

  const normalizedSupplierUuid = normalizeOptionalString(
    supplierUuid,
  );

  if (normalizedSupplierUuid) {
    params.supplier_uuid = normalizedSupplierUuid;
  }

  const normalizedProductUuid = normalizeOptionalString(
    productUuid,
  );

  if (normalizedProductUuid) {
    params.product_uuid = normalizedProductUuid;
  }

  const normalizedStatus = normalizeSupportedValue(
    status,
    INVENTORY_STATUSES,
    "",
  );

  if (normalizedStatus) {
    params.status = normalizedStatus;
  }

  return params;
}


export function getInventoryErrorMessage(
  error,
  fallback = "Unable to process the inventory request.",
) {
  const responseData = error?.response?.data;

  if (
    typeof responseData?.message === "string"
    && responseData.message.trim()
  ) {
    return responseData.message.trim();
  }

  if (
    typeof responseData?.error?.message === "string"
    && responseData.error.message.trim()
  ) {
    return responseData.error.message.trim();
  }

  if (
    typeof responseData?.error === "string"
    && responseData.error.trim()
  ) {
    return responseData.error.trim();
  }

  if (
    typeof error?.message === "string"
    && error.message.trim()
  ) {
    return error.message.trim();
  }

  return fallback;
}


export async function listInventory({
  companyUuid,
  activityUuid,
  page = 1,
  pageSize = DEFAULT_INVENTORY_PAGE_SIZE,
  search = "",
  supplierUuid = "",
  productUuid = "",
  status = "",
  sort = "product",
  direction = "asc",
} = {}) {
  const response = await api.get(
    "/supply-chain/inventory",
    {
      headers: {
        ...buildCompanyScopeHeaders(
          companyUuid,
        ),
        ...buildActivityScopeHeaders(
          activityUuid,
        ),
      },
      params: buildInventoryListParams({
        page,
        pageSize,
        search,
        supplierUuid,
        productUuid,
        status,
        sort,
        direction,
      }),
    },
  );

  return response.data;
}


export async function getInventoryProduct(
  productUuid,
  {
    companyUuid,
  activityUuid,
  } = {},
) {
  const normalizedProductUuid = requireNonEmptyString(
    productUuid,
    "productUuid",
  );

  const response = await api.get(
    `/supply-chain/inventory/${encodeURIComponent(
      normalizedProductUuid,
    )}`,
    {
      headers: {
        ...buildCompanyScopeHeaders(
          companyUuid,
        ),
        ...buildActivityScopeHeaders(
          activityUuid,
        ),
      },
    },
  );

  return response.data;
}
